import { rgb, PDFDocument } from 'pdf-lib';
import fontkit from '@pdf-lib/fontkit';
import api from 'utils/api';
import { pdf } from '@react-pdf/renderer';
import logger from 'utils/logger';
import { DOLPHIN_API } from 'shared/constants/apis';
import localData from 'utils/localData';
import { REPORT_TEMPLATE_LIST } from 'components/Report/Templates/constants';
import { LANGUAGE_CODE, getCurrentLocale } from './intl';
import { USER_DATA } from 'shared/constants/users';

const log = logger.createLogger('ReportUtils');

const imageExtensions = {
  ['png']: 'embedPng',
  ['jpg']: 'embedJpg',
  ['jpeg']: 'embedJpg',
};

export const REPORT_ELEMENT_TYPE = {
  MULTILINE_TEXT: 'multiline-text',
  TEXT: 'text',
  DATE: 'date',
  DND: 'dnd',
  IMAGE: 'image',
  SELECT_TEXT: 'select-text',
  SELECT_OBJECT: 'select-object',
  NUMBER: 'number',
  BLANK: 'blank',
};

const TEXT_ALIGN = {
  TOP_LEFT: 'top-left',
  CENTER: 'center',
};

const FONT_WEIGHTS = {
  LIGHT: 'Light',
  REGULAR: 'Regular',
  MEDIUM: 'Medium',
  SEMIBOLD: 'SemiBold',
  BOLD: 'Bold',
  EXTRABOLD: 'ExtraBold',
  BLACK: 'Black',
};

const COLORS = {
  WHITE: 'white',
  BLACK: 'black',
  GRAY: 'gray',
  GREY: 'grey',
};

const COLOR_MAP = {
  [COLORS.WHITE]: [1, 1, 1],
  [COLORS.BLACK]: [0, 0, 0],
  [COLORS.GRAY]: [0.9, 0.9, 0.9],
  [COLORS.GREY]: [0.9, 0.9, 0.9],
};

const SELECTED_FONT_WEIGHT = FONT_WEIGHTS.REGULAR;

const FONT_CONFIG = {
  [LANGUAGE_CODE.ENGLISH]: {
    getPath: (weight = SELECTED_FONT_WEIGHT) =>
      `Noto_Sans/static/NotoSans-${weight}.ttf`,
    fallback: 'Nasu/NotoSans-Regular.ttf',
  },
  [LANGUAGE_CODE.JAPANESE]: {
    getPath: (weight = SELECTED_FONT_WEIGHT) =>
      `Noto_Sans_JP/static/NotoSansJP-${weight}.ttf`,
    fallback: 'Nasu/Nasu-Regular.ttf',
  },
  [LANGUAGE_CODE.CHINESE]: {
    getPath: (weight = SELECTED_FONT_WEIGHT) =>
      `Noto_Sans_SC/static/NotoSansSC-${weight}.ttf`,
    fallback: 'Noto_Sans/NotoSans-VariableFont_wdth,wght.ttf',
  },
  [LANGUAGE_CODE.INDONESIAN]: {
    getPath: (weight = SELECTED_FONT_WEIGHT) =>
      `Noto_Sans/static/NotoSans-${weight}.ttf`,
    fallback: 'Nasu/NotoSans-Regular.ttf',
  },
};

export const getPresignedURL = async (reportTemplateId, reportImageURL) => {
  const response = await api.post(
    `${DOLPHIN_API.DOWNLOAD}/template/${reportTemplateId}`,
    {
      params: {
        organizationId: localData.get(USER_DATA.CURRENT_ORGANIZATION_ID),
      },
      data: {
        reportTemplateImageURL: reportImageURL,
      },
    }
  );
  return response.GetObjectPresignedURL;
};

const getTextAlignCoordinates = (
  textAlign,
  x,
  y,
  width,
  height,
  fontSize,
  value,
  font
) => {
  let finalX = x;
  let finalY = y;

  //! Other offsets need to be added on demand
  switch (textAlign) {
    case TEXT_ALIGN.CENTER: {
      const textWidth = font.widthOfTextAtSize(value, fontSize);
      const textHeight = font.heightAtSize(fontSize);
      finalX = x + width / 2 - textWidth / 2;
      const baselineOffset = textHeight * 0.3;
      finalY = y + height / 2 - textHeight / 2 + baselineOffset;
      break;
    }
    case TEXT_ALIGN.TOP_LEFT: {
      const textHeight = font.heightAtSize(fontSize);
      const fontOffset = textHeight * 0.8;
      finalY = y + height - fontOffset;
      break;
    }
    default:
      log.warn('No text align or unknown text align', textAlign);
      break;
  }

  return { finalX, finalY };
};

const loadFontForLocale = async (locale, getStaticPath, pdfDoc) => {
  const fontConfig = FONT_CONFIG[locale] || FONT_CONFIG[LANGUAGE_CODE.ENGLISH];

  try {
    const fontBytes = await fetch(
      `${getStaticPath()}/${fontConfig.getPath()}`
    ).then((res) => {
      if (!res.ok) throw new Error(`Failed to load font: ${res.status}`);
      return res.arrayBuffer();
    });

    return await pdfDoc.embedFont(fontBytes);
  } catch (error) {
    log.warn(
      `Failed to load primary font for ${locale}, trying fallback:`,
      error
    );

    try {
      const fallbackFontBytes = await fetch(
        `${getStaticPath()}/${fontConfig.fallback}`
      ).then((res) => {
        if (!res.ok)
          throw new Error(`Failed to load fallback font: ${res.status}`);
        return res.arrayBuffer();
      });

      return await pdfDoc.embedFont(fallbackFontBytes);
    } catch (fallbackError) {
      log.error(
        `Failed to load both primary and fallback fonts for ${locale}:`,
        fallbackError
      );

      const basicFontBytes = await fetch(
        `${getStaticPath()}/Nasu/NotoSans-Regular.ttf`
      ).then((res) => res.arrayBuffer());

      return await pdfDoc.embedFont(basicFontBytes);
    }
  }
};

const detectLanguage = (text) => {
  if (!text || typeof text !== 'string') return LANGUAGE_CODE.ENGLISH;

  // Japanese character ranges in Unicode
  const hiragana = /[\u3040-\u309F]/; // Hiragana
  const katakana = /[\u30A0-\u30FF]/; // Katakana
  const japaneseKanji = /[\u4E00-\u9FAF\u3400-\u4DBF]/; // CJK Unified Ideographs

  // Chinese character ranges in Unicode
  const chineseSimplified = /[\u4E00-\u9FFF]/; // CJK Unified Ideographs (includes both Chinese and Japanese Kanji)
  const chineseTraditional = /[\u3400-\u4DBF]/; // CJK Extension A

  // English/Latin character ranges
  const latin = /[a-zA-Z]/;

  // Count character types
  let japaneseCount = 0;
  let chineseCount = 0;
  let latinCount = 0;

  for (let i = 0; i < text.length; i++) {
    const char = text[i];

    if (hiragana.test(char) || katakana.test(char)) {
      japaneseCount++;
    } else if (japaneseKanji.test(char)) {
      // Kanji can be either Japanese or Chinese
      // If we have hiragana/katakana in the text, it's likely Japanese
      if (hiragana.test(text) || katakana.test(text)) {
        japaneseCount++;
      } else {
        chineseCount++;
      }
    } else if (chineseSimplified.test(char) || chineseTraditional.test(char)) {
      chineseCount++;
    } else if (latin.test(char)) {
      latinCount++;
    }
  }

  // Determine language based on character counts
  const totalChars = japaneseCount + chineseCount + latinCount;
  if (totalChars === 0) return LANGUAGE_CODE.ENGLISH;
  if (japaneseCount > 0) {
    return LANGUAGE_CODE.JAPANESE;
  }
  if (chineseCount > 0) {
    return LANGUAGE_CODE.CHINESE;
  }
  // Default to English for Latin characters or mixed content
  return LANGUAGE_CODE.ENGLISH;
};

const getFontForLanguage = (
  detectedLanguage,
  defaultFont,
  japaneseFont,
  chineseFont
) => {
  switch (detectedLanguage) {
    case LANGUAGE_CODE.JAPANESE:
      return japaneseFont;
    case LANGUAGE_CODE.CHINESE:
      return chineseFont;
    case LANGUAGE_CODE.ENGLISH:
    default:
      return defaultFont;
  }
};

export const loadPdfFromPresignedURL = async (
  objectPresignedURL,
  reportContent,
  debugBox
) => {
  const locale = getCurrentLocale();
  const reportId = reportContent.id;
  const existingPdfBytes = await fetch(objectPresignedURL).then((res) =>
    res.arrayBuffer()
  );
  const pdfDoc = await PDFDocument.load(existingPdfBytes);

  const getStaticFontPath = () => {
    const publicUrl = import.meta.env.VITE_PUBLIC_URL || '';
    // Remove trailing slash if present
    const cleanPublicUrl = publicUrl.replace(/\/$/, '');
    return `${cleanPublicUrl}/static/font`;
  };

  // Register fontkit for all languages as we cannot assure by locale
  pdfDoc.registerFontkit(fontkit);
  const japaneseFont = await loadFontForLocale(
    LANGUAGE_CODE.JAPANESE,
    getStaticFontPath,
    pdfDoc
  );
  const chineseFont = await loadFontForLocale(
    LANGUAGE_CODE.CHINESE,
    getStaticFontPath,
    pdfDoc
  );
  const defaultFont = await loadFontForLocale(
    LANGUAGE_CODE.ENGLISH,
    getStaticFontPath,
    pdfDoc
  );

  const pages = pdfDoc.getPages();

  //! For debugging purposes
  if (debugBox) {
    const page = pages[0];
    page.drawRectangle({
      x: debugBox.x,
      y: debugBox.y,
      width: debugBox.width,
      height: debugBox.height,
      borderWidth: 2,
      borderColor: rgb(0, 0, 1),
      borderStyle: 'solid',
    });
  }
  for (const setting of reportContent.content) {
    const { type, value, pageIndex, x, y, hidden } = setting;
    const page = pages[pageIndex];
    const font = getFontForLanguage(
      detectLanguage(value),
      defaultFont,
      japaneseFont,
      chineseFont
    );

    if (hidden) {
      continue;
    }
    switch (type) {
      case REPORT_ELEMENT_TYPE.TEXT: {
        const { textSettings } = setting;
        // Check if text fits within width, reduce font size if needed
        let currentFontSize = parseInt(textSettings.fontSize);
        let textWidth = font.widthOfTextAtSize(value, currentFontSize);

        // Reduce font size until text fits within width
        while (textWidth > textSettings.width && currentFontSize > 6) {
          // Minimum font size of 6
          currentFontSize -= 1;
          textWidth = font.widthOfTextAtSize(value, currentFontSize);
        }

        const { finalX, finalY } = getTextAlignCoordinates(
          textSettings.textAlign || 'null',
          x,
          y,
          textSettings.width,
          textSettings.height,
          currentFontSize, // Use the adjusted font size
          value,
          font
        );

        page.drawText(value, {
          x: finalX,
          y: finalY,
          font: font,
          lineHeight: Math.max(
            textSettings.lineHeight || 0,
            font.heightAtSize(currentFontSize) * 1.2 // Use adjusted font size for line height too
          ),
          color: rgb(0.29, 0.29, 0.29),
          size: currentFontSize, // Use the adjusted font size
        });

        break;
      }
      case REPORT_ELEMENT_TYPE.NUMBER: {
        const { numberSettings } = setting;
        const { finalX, finalY } = getTextAlignCoordinates(
          numberSettings.textAlign || 'null',
          x,
          y,
          numberSettings.width,
          numberSettings.height,
          numberSettings.fontSize,
          value,
          font
        );

        //! In order to avoid the issue of halfwidth and fullwith charts
        page.drawText(value, {
          x: finalX,
          y: finalY,
          font: font,
          color: rgb(0.29, 0.29, 0.29),
          size: parseInt(numberSettings.fontSize),
        });
        break;
      }
      case REPORT_ELEMENT_TYPE.SELECT_TEXT: {
        const { selectSettings, value } = setting;
        const { fontSize, textAlign, width, height } = selectSettings;
        const valueString = value.toString();
        const { finalX, finalY } = getTextAlignCoordinates(
          textAlign,
          x,
          y,
          width,
          height,
          fontSize,
          valueString,
          font
        );

        page.drawText(valueString, {
          x: finalX,
          y: finalY,
          font: font,
          color: rgb(0.29, 0.29, 0.29),
          size: parseInt(fontSize),
        });

        break;
      }
      case REPORT_ELEMENT_TYPE.IMAGE: {
        const { imageSettings } = setting;
        const image = await loadImage(value, pdfDoc);
        if (image) {
          page.drawImage(image, {
            x: x,
            y: y,
            width: imageSettings.width,
            height: imageSettings.height,
          });
        }
        break;
      }
      case REPORT_ELEMENT_TYPE.DND:
        {
          const { dndElements, height, width, naturalWidth, naturalHeight } =
            setting;

          if (dndElements.length === 0) {
            break;
          }
          const pdfContainerWidth = width;
          const pdfContainerHeight = height;

          const originalImageNaturalWidth = naturalWidth;
          const originalImageNaturalHeight = naturalHeight;

          const imageAspectRatio =
            originalImageNaturalWidth / originalImageNaturalHeight;
          const pdfContainerAspectRatio =
            pdfContainerWidth / pdfContainerHeight;

          let pdfDisplayedImageWidth;
          let pdfDisplayedImageHeight;
          let pdfOffsetX = 0; // Offset from the left edge of the PDF container
          let pdfOffsetY = 0; // Offset from the bottom edge of the PDF container (PDF y-axis is bottom-up)

          if (imageAspectRatio > pdfContainerAspectRatio) {
            // Image is wider, constrained by container width
            pdfDisplayedImageWidth = pdfContainerWidth;
            pdfDisplayedImageHeight = pdfContainerWidth / imageAspectRatio;
            // Vertical offset (letterboxing)
            pdfOffsetY = (pdfContainerHeight - pdfDisplayedImageHeight) / 2;
          } else {
            // Image is taller or proportional, constrained by container height
            pdfDisplayedImageHeight = pdfContainerHeight;
            pdfDisplayedImageWidth = pdfContainerHeight * imageAspectRatio;
            // Horizontal offset (pillarboxing)
            pdfOffsetX = (pdfContainerWidth - pdfDisplayedImageWidth) / 2;
          }

          for (const element of dndElements) {
            const {
              type,
              imagePath,
              x: xPercent,
              y: yPercent,
              imageSettings,
            } = element;

            if (type === REPORT_ELEMENT_TYPE.IMAGE) {
              const image = await loadImage(imagePath, pdfDoc, reportId);
              if (image) {
                const centerX_on_pdf_displayed =
                  (xPercent / 100) * pdfDisplayedImageWidth;
                const centerY_on_pdf_displayed =
                  (yPercent / 100) * pdfDisplayedImageHeight;

                const elementPixelWidth = imageSettings.width; // Assuming these are fixed for your elements
                const elementPixelHeight = imageSettings.height;

                // Calculate top-left for PDF:
                // X: Start with container's X, add horizontal offset, add center X on displayed image, subtract half element width
                const xToDraw =
                  x +
                  pdfOffsetX +
                  centerX_on_pdf_displayed -
                  elementPixelWidth / 2;

                const yToDraw =
                  y +
                  pdfOffsetY +
                  (pdfDisplayedImageHeight - centerY_on_pdf_displayed) -
                  elementPixelHeight / 2;

                page.drawImage(image, {
                  x: xToDraw,
                  y: yToDraw,
                  width: imageSettings.width,
                  height: imageSettings.height,
                });
              }
            }
          }
        }
        break;
      case REPORT_ELEMENT_TYPE.SELECT_OBJECT: {
        const { selectSettings } = setting;

        for (const option of selectSettings.elements) {
          // Check if value is the same as the option
          if (option.value === value) {
            page.drawCircle({
              x: x + option.x,
              y: y + option.y,
              size: option.size,
              borderWidth: 1,
              borderColor: rgb(0, 0, 1),
              borderStyle: 'solid',
            });
          }
        }
        break;
      }
      case REPORT_ELEMENT_TYPE.DATE: {
        const { dateSettings } = setting;
        const { fontSize, textAlign, width, height } = dateSettings;

        if (!value) {
          break;
        }

        let dateValue;
        if (value instanceof Date) {
          dateValue = value;
        } else {
          dateValue = new Date(value);
        }

        const valueString = dateValue.toLocaleDateString(locale, {
          year: 'numeric',
          month: 'long',
          day: 'numeric',
        });

        // Override as the format needs to be added
        const font = getFontForLanguage(
          detectLanguage(valueString),
          defaultFont,
          japaneseFont,
          chineseFont
        );
        const { finalX, finalY } = getTextAlignCoordinates(
          textAlign,
          x,
          y,
          width,
          height,
          fontSize,
          valueString,
          font
        );

        page.drawText(valueString, {
          x: finalX,
          y: finalY,
          font: font,
          color: rgb(0.29, 0.29, 0.29),
          size: parseInt(fontSize),
        });
        break;
      }
      case REPORT_ELEMENT_TYPE.BLANK: {
        const { blankSettings } = setting;
        const { width, height, color } = blankSettings;
        const colorValues = COLOR_MAP[color] || COLOR_MAP[COLORS.GRAY];

        page.drawRectangle({
          x: x,
          y: y,
          width: width,
          height: height,
          borderWidth: 0,
          color: rgb(...colorValues),
          borderColor: rgb(...colorValues),
        });
        break;
      }
      default:
        break;
    }
  }

  const pdfBytes = await pdfDoc.save();
  const blob = new Blob([pdfBytes], { type: 'application/pdf' });
  const url = URL.createObjectURL(blob);
  return url;
};

const loadImage = async (imagePath, pdfDoc, reportId) => {
  const presignedUrl = await getPresignedURL(reportId, imagePath);
  const imageBytes = await fetch(presignedUrl, {
    cache: 'no-store', // Or 'reload'
  }).then((res) => res.arrayBuffer());
  let image;
  // Since it's a presigned url, we need to get the extension from the url
  // Remove the query params
  const extension = imagePath.split('.').pop().toLowerCase();
  if (imageExtensions[extension]) {
    image = await pdfDoc[imageExtensions[extension]](imageBytes);
  } else {
    log.error('Unsupported image format', imagePath);
  }
  return image;
};

export const generatePDFFromTemplate = async (
  template_id,
  reportContent,
  reportTitle
) => {
  const templates = REPORT_TEMPLATE_LIST();
  const template = templates.find((template) => template.id === template_id);

  if (!template) {
    throw new Error('Template not found');
  }

  const blob = await pdf(
    <template.template
      reportContent={reportContent}
      reportTitle={reportTitle}
    />
  ).toBlob();
  const url = URL.createObjectURL(blob);
  return url;
};
